import React from 'react'
import classNames from 'classnames'
import dynamic from 'next/dynamic'

import 'project/polyfill'
import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import Strings from 'project/localisation'
import { sectionGap } from 'project/padding'
import useWindowSize from 'components/util/useWindowSize'
import ContactUsCard from 'components/cards/new/ContactUsCard'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import { homeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import LondonVOPlusHeroCard from 'components/cards/new/LondonVOPlusHeroCard'
import EastLondonTechCityCard from 'components/cards/new/EastLondonTechCityCard'
import { siliconRoundaboutStrings } from 'common/strings/silicon-roundabout-strings'
import { I18NConfig } from 'next/dist/server/config-shared'
import { createClient } from '../prismicio'
import { Utils } from 'common/utils'
import { getPlans } from 'common/planData'
import { TPlanSummary } from 'common/cms-types'
import { PlanSlug } from 'common/planSlugs'
import HeroBanner from 'components/hero/HeroBanner'
import HoxtonMixLogo from 'components/icons/HoxtonMixLogo'

const MyMap = dynamic(() => import('components/map/MyMap'), {
  ssr: false,
})

export type PageType = {
  plans: TPlanSummary[]
}
const Page: NextPageWithLayout<PageType> = ({ plans }) => {
  const seoProps: SeoContainerProps['seoProps'] = {
    description: siliconRoundaboutStrings.seoDescription,
    openGraph: {
      url: 'https://www.hoxtonmix.com/silicon-roundabout/',
    },
    title: siliconRoundaboutStrings.seoTitle,
  }
  const breadcrumb = homeBreadcrumb().concat([
    {
      item: 'https://www.hoxtonmix.com/silicon-roundabout/',
      name: `${Strings.siliconRoundabout}`,
      position: 2,
    },
  ])

  const size = useWindowSize()
  return (
    <SeoContainer breadcrumb={breadcrumb} seoProps={seoProps}>
      <main className='overflow-x-hidden new-design d-flex flex-column'>
        <HeroBanner
          logo={<HoxtonMixLogo className='mb-5 mb-lg-8' />}
          robotImage={{
            alt: 'Hoxton Mix Virtual Office London Logo',
            dimensions: {
              height: 878,
              width: 681,
            },
            size: 'small',
            src: '/images/new/robot-holding-mail.png',
          }}
          title={Strings.siliconRoundabout}
          subtitle={
            <span className='fs-fluid-24-48'>
              {Strings.londonsTechnologyHub}
            </span>
          }
          description=''
          hideDownloadButtons
          hideFooter
        />
        <div
          className={classNames(
            'container d-flex flex-column mt-lg-n20 mb-15 mb-lg-36',
            sectionGap,
          )}
        >
          <div
            className='d-flex flex-column text-center mx-auto'
            style={{
              maxWidth: 1154,
            }}
          >
            <h2 className='fs-fluid-36-80 fw-semibold mb-lg-3 mb-3 lh-105'>
              {siliconRoundaboutStrings.europesAnswer}
            </h2>
            <span
              className='fs-fluid-18-28 color-grey5 lh-114 fw-semibold'
              dangerouslySetInnerHTML={{
                __html: `${siliconRoundaboutStrings.europesAnswerDescription}`,
              }}
            />
          </div>
          <EastLondonTechCityCard />
          <div>
            <div
              className='d-flex flex-column text-center mx-auto'
              style={{
                maxWidth: 1028,
              }}
            >
              <h2
                className='fs-fluid-36-80 fw-semibold mb-lg-3 mb-3 lh-105'
                dangerouslySetInnerHTML={{
                  __html: `${siliconRoundaboutStrings.introductionToTechCity}`,
                }}
              />
              <span className='fs-fluid-18-28 color-grey5 lh-114 fw-semibold d-block mb-4 mb-lg-0'>
                {siliconRoundaboutStrings.introductionToTechCityDescription}
              </span>
              <span className='fs-fluid-18-28 color-grey5 lh-114 fw-semibold'>
                {siliconRoundaboutStrings.introductionToTechCityContent}
              </span>
            </div>
            <video
              poster='/images/hoxton_mix_tech_city_tour.jpeg'
              id='my-video_html5_api'
              className='card w-100 mt-6 mt-lg-13'
              preload='auto'
              controls
              data-setup='{}'
            >
              <source
                src='https://storage.googleapis.com/blog-assets.hoxtonmix.com/Hoxton_Mix_Tech_City.mp4'
                type='video/mp4'
              />
              <p className='vjs-no-js'>
                {' '}
                To view this video please enable JavaScript, and consider
                upgrading to a web browser that{' '}
                <a
                  href='http://videojs.com/html5-video-support/'
                  target='_blank'
                  rel='noreferrer'
                >
                  supports HTML5 video
                </a>
              </p>
            </video>
          </div>
          <h2
            className='fw-semibold d-none d-lg-block fs-fluid-36-80 lh-105 text-center mx-auto'
            style={{
              maxWidth: 938,
            }}
          >
            {siliconRoundaboutStrings.coolPlaceToRunBusiness}
          </h2>
          <div className='row gy-5'>
            <div className='col-12 col-lg-6 '>
              <ContactUsCard />
            </div>
            <div className='col-12 col-lg-6'>
              <LondonVOPlusHeroCard
                planSummary={plans.find((v) => v.slug === PlanSlug.SCAN_LITE)!}
              />
            </div>
          </div>
        </div>
        <h2 className='fw-semibold d-none d-lg-block fs-fluid-36-50 lh-105 text-center mx-auto mb-12'>
          The Hoxton Mix offices are strategically located{' '}
          <br className='d-none d-lg-block' /> in the heart of Silicon
          roundabout.
        </h2>
      </main>
      <div className='mb-6'>
        <MyMap />
      </div>
    </SeoContainer>
  )
}

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)
    const plans = await getPlans()
    return {
      props: {
        plans,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      props: {},
    }
  }
}
export default Page
